// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    FormFieldCheckbox,
    FormFieldText,
    Section,
    Translate,
} from '@plesk/ui-library';
import SettingsWrapper from 'admin/settings/containers/Settings/SettingsWrapper';
import * as settingsActions from 'common/modules/settings/actions';
import { connect } from 'react-redux';
import {
    Dispatch,
    bindActionCreators,
} from 'redux';
import { ISettingsResponse } from 'common/api/resources/Settings';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import {
    intMinRule,
    validate,
} from 'common/validator';
import { SIZE } from 'common/constants';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';
import { RootState } from 'admin/core/store';
import { isFeatureFlagEnabled } from 'common/modules/app/features/utils';
import { FEATURES } from 'common/modules/app/features/constants';
import NotFound from 'common/components/ErrorPages/NotFound/NotFound';

export type HighAvailabilitySettingsProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const GRACE_PERIOD_MIN: number = 0;
const MAX_CONCURRENT_FAILURES_MIN: number = 1;

export const HighAvailabilitySettings: React.FC<HighAvailabilitySettingsProps> = ({
    isHighAvailabilityEnabled,
    settingsActions: { saveSettings },
    formErrorsActions: { setFormErrors },
}) => {

    React.useEffect(() => {
        if (isHighAvailabilityEnabled) {
            track(ANALYTICS_EVENTS.ADMIN.SETTINGS.HIGH_AVAILABILITY.VISIT);
        }
    }, [isHighAvailabilityEnabled]);

    if (!isHighAvailabilityEnabled) {
        return <NotFound />;
    }

    const handleSubmit = (values: ISettingsResponse) => {
        const formErrors = validate<ISettingsResponse>(values, {
            'high_availability_cr.grace_period_seconds': intMinRule(<Translate content="validate.fieldIntMin" params={{ min: GRACE_PERIOD_MIN }} />, GRACE_PERIOD_MIN),
            'high_availability_cr.max_concurrent_failures': intMinRule(<Translate content="validate.fieldIntMin" params={{ min: MAX_CONCURRENT_FAILURES_MIN }} />, MAX_CONCURRENT_FAILURES_MIN),
        });

        if (Object.keys(formErrors).length) {
            setFormErrors(formErrors);
            return;
        }

        saveSettings({
            high_availability_cr: values.high_availability_cr,
        });
    };

    return (
        <SettingsWrapper
            title="settings.titles.highAvailability"
            onSubmit={handleSubmit}
        >
            <Section>
                <FormFieldCheckbox
                    name="high_availability_cr[enabled]"
                    label={<Translate content="settings.highAvailabilityCr.enabled" />}
                />
                <FormFieldText
                    name="high_availability_cr[grace_period_seconds]"
                    size={SIZE.MD}
                    label={<Translate content="settings.highAvailabilityCr.gracePeriodSeconds" />}
                    required={true}
                    inputProps={{
                        type: 'number',
                        inputMode: 'numeric',
                    }}
                />
                <FormFieldText
                    name="high_availability_cr[max_concurrent_failures]"
                    size={SIZE.MD}
                    label={<Translate content="settings.highAvailabilityCr.maxConcurrentFailures" />}
                    required={true}
                    inputProps={{
                        type: 'number',
                        inputMode: 'numeric',
                    }}
                />
            </Section>
        </SettingsWrapper>
    );
};

const mapStateToProps = (state: RootState) => ({
    isHighAvailabilityEnabled: isFeatureFlagEnabled(state, FEATURES.HIGH_AVAILABILITY_CR),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    settingsActions: bindActionCreators(settingsActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(HighAvailabilitySettings);
